import { motion } from "framer-motion";
import { Clock } from "lucide-react";
import { Reveal } from "./Reveal";

const DAYS = [
    {
        day: "Day 1",
        title: "Spot the Distance",
        desc: "Understand where emotional distance may be showing up.",
        span: "lg:col-span-2",
        dark: false,
    },
    {
        day: "Day 2",
        title: "Find Your Relationship Energy Leaks",
        desc: "Identify habits, reactions and patterns that may be draining connection.",
        span: "lg:col-span-2",
        dark: true,
    },
    {
        day: "Day 3",
        title: "Pause Before You React",
        desc: "Simple reflection prompts to understand your own emotional triggers.",
        span: "lg:col-span-2",
        dark: false,
    },
    {
        day: "Day 4",
        title: "Reconnect",
        desc: "Small actions designed to create space for better communication and connection.",
        span: "lg:col-span-3",
        dark: false,
    },
    {
        day: "Day 5",
        title: "Your Reset Plan",
        desc: "Create a simple personal plan for continuing the changes beyond the 5 days.",
        span: "lg:col-span-3",
        dark: false,
    },
];

export const WhatsInside = () => (
    <section className="relative bg-ivory py-16 sm:py-24" data-testid="whats-inside-section">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
            <Reveal className="text-center">
                <h2 className="mx-auto max-w-2xl font-serif text-2xl font-medium leading-snug text-plum sm:text-3xl lg:text-4xl">
                    What&rsquo;s Inside Your <em className="italic text-burgundy">&#8377;49</em> Guide?
                </h2>
            </Reveal>

            <motion.div
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.12 }}
                variants={{ hidden: {}, show: { transition: { staggerChildren: 0.09 } } }}
                className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-6"
                data-testid="day-cards-grid"
            >
                {DAYS.map((d, i) => (
                    <motion.div
                        key={d.day}
                        variants={{
                            hidden: { opacity: 0, y: 28 },
                            show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
                        }}
                        whileHover={{ y: -6 }}
                        className={`h-full rounded-2xl p-6 sm:p-7 ${
                            d.dark
                                ? "bg-plum text-cream shadow-xl shadow-plum/25"
                                : "border border-blushline bg-white shadow-sm hover:shadow-md"
                        } ${d.span}`}
                        data-testid={`day-card-${i + 1}`}
                    >
                        <p className={`font-serif text-4xl italic ${d.dark ? "text-gold" : "text-gold/80"}`}>
                            {String(i + 1).padStart(2, "0")}
                        </p>
                        <p
                            className={`mt-4 font-sans text-[0.6rem] font-bold uppercase tracking-[0.26em] ${
                                d.dark ? "text-gold" : "text-burgundy"
                            }`}
                        >
                            {d.day}
                        </p>
                        <h3 className="mt-2 font-serif text-xl font-medium leading-snug sm:text-2xl">{d.title}</h3>
                        <p className={`mt-3 text-[0.95rem] leading-relaxed ${d.dark ? "text-cream/80" : "text-mutedtext"}`}>
                            {d.desc}
                        </p>
                    </motion.div>
                ))}
            </motion.div>

            <Reveal delay={0.15} className="mt-10 text-center">
                <p
                    className="inline-flex items-center gap-2.5 rounded-full bg-blush px-5 py-2.5 font-sans text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-burgundy"
                    data-testid="whats-inside-note"
                >
                    <Clock className="h-3.5 w-3.5 text-gold" />
                    Designed to take just a few minutes each day.
                </p>
            </Reveal>
        </div>
    </section>
);

export default WhatsInside;
